import React, { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useParams, Link, useNavigate } from 'react-router-dom'
import {
	Button,
	Table,
	Title,
	Group,
	Badge,
	Stack,
	Loader,
	Progress,
	Text,
} from '@mantine/core'
import { listBlasts } from '../services/actions/messagingActions'

const replaceAll = (value, needle, replacement) =>
	String(value || '')
		.split(needle)
		.join(replacement)

const getNonEmptyTrimmedValue = (...values) => {
	for (const value of values) {
		if (typeof value !== 'string') continue
		const normalized = value.trim()
		if (normalized) return normalized
	}
	return null
}

const IN_PROGRESS_STATUSES = new Set(['QUEUED', 'SENDING'])
const POLL_INTERVAL_MS = 5000

const toSafeCount = (value) => {
	const parsed = Number(value)
	if (!Number.isFinite(parsed)) return 0
	return Math.max(0, Math.floor(parsed))
}

const getTotalRecipientsForBlast = (blast) =>
	Math.max(toSafeCount(blast?.totalRecipients), toSafeCount(blast?._count?.recipients))

const getBlastScheduledSendTime = (blast) => {
	if (!blast?.scheduledAt) return null
	const date = new Date(blast.scheduledAt)
	return Number.isFinite(date.getTime()) ? date : null
}

export const getBlastProgress = (blast) => {
	const totalRecipients = getTotalRecipientsForBlast(blast)
	const legacyProcessedCount =
		toSafeCount(blast?.sentCount) +
		toSafeCount(blast?.deliveredCount) +
		toSafeCount(blast?.failedCount) +
		toSafeCount(blast?.openedCount) +
		toSafeCount(blast?.clickedCount) +
		toSafeCount(blast?.bouncedCount) +
		toSafeCount(blast?.unsubCount)
	const queuedCount = Math.min(
		totalRecipients,
		toSafeCount(
			blast?.queuedCount ??
				blast?.recipientCounts?.QUEUED ??
				Math.max(0, totalRecipients - legacyProcessedCount),
		),
	)
	const processedCount = Math.min(
		totalRecipients,
		toSafeCount(blast?.processedCount ?? totalRecipients - queuedCount),
	)
	const unsuccessfulCount = Math.min(
		processedCount,
		toSafeCount(
			blast?.unsuccessfulCount ??
				toSafeCount(blast?.failedCount) +
					toSafeCount(blast?.bouncedCount) +
					toSafeCount(blast?.unsubCount),
		),
	)

	return { totalRecipients, queuedCount, processedCount, unsuccessfulCount }
}

export const formatSendDuration = (blast) => {
	if (!blast?.lastSentAt) return '—'
	const triggeredAt = new Date(blast?.scheduledAt || blast?.createdAt).getTime()
	const lastSentAt = new Date(blast?.lastSentAt).getTime()
	if (!Number.isFinite(triggeredAt) || !Number.isFinite(lastSentAt)) return '—'

	const totalSeconds = Math.max(0, Math.round((lastSentAt - triggeredAt) / 1000))
	const hours = Math.floor(totalSeconds / 3600)
	const minutes = Math.floor((totalSeconds % 3600) / 60)
	const seconds = totalSeconds % 60
	if (hours > 0) return `${hours}h ${minutes}m`
	if (minutes > 0) return `${minutes}m ${seconds}s`
	return `${seconds}s`
}

export default function MessagingCenterPage() {
	const { orgSlug, experienceSlug } = useParams()
	const navigate = useNavigate()
	const experience = useSelector((state) => state.experience?.experienceDetails)
	const currentExperience = useSelector((state) => state.user?.currentExperience)
	const currentOrg = useSelector((state) => state.user?.currentOrg)
	const [blasts, setBlasts] = useState([])
	const [loading, setLoading] = useState(true)
	const [loadError, setLoadError] = useState('')
	const resolvedExperienceName =
		getNonEmptyTrimmedValue(
			experience?.shortName,
			currentExperience?.shortName,
			experience?.name,
			currentExperience?.name,
			experienceSlug,
		) || 'Experience'

	const renderSubject = (subject) => {
		if (!subject) return '—'
		let output = String(subject)
		output = replaceAll(output, '{{experienceName}}', resolvedExperienceName)
		output = replaceAll(output, '{{orgName}}', currentOrg?.orgName || '')
		return output
	}

	const renderUndelivered = (blast) => {
		const categories = [
			{ label: 'Failed', count: toSafeCount(blast.failedCount) },
			{ label: 'Bounced', count: toSafeCount(blast.bouncedCount) },
			{ label: 'Unsubscribed', count: toSafeCount(blast.unsubCount) },
		].filter(({ count }) => count > 0)

		if (categories.length === 0) return <Text size='sm'>0 Undelivered</Text>
		return (
			<Stack spacing={0}>
				{categories.map(({ label, count }) => (
					<Text size='sm' key={label}>{count} {label}</Text>
				))}
			</Stack>
		)
	}

	const loadBlasts = useCallback(
		async ({ signal, background = false } = {}) => {
			try {
				const data = await listBlasts(orgSlug, experienceSlug, { signal })
				setBlasts(Array.isArray(data) ? data : [])
				setLoadError('')
			} catch (err) {
				if (err?.name === 'AbortError') return
				console.error(err)
				if (!background) {
					setLoadError(err.message || 'Failed to load messaging blasts.')
				}
			}
		},
		[orgSlug, experienceSlug],
	)

	useEffect(() => {
		const controller = new AbortController()
		setLoading(true)
		loadBlasts({ signal: controller.signal }).finally(() => {
			if (!controller.signal.aborted) setLoading(false)
		})
		return () => controller.abort()
	}, [loadBlasts])

	const hasInProgressBlasts = blasts.some((blast) =>
		IN_PROGRESS_STATUSES.has(String(blast?.status || '').toUpperCase()),
	)
	useEffect(() => {
		if (!hasInProgressBlasts) return
		let controller = null
		const refreshIfVisible = () => {
			if (controller) return
			if (
				typeof document !== 'undefined' &&
				document.visibilityState !== 'visible'
			) {
				return
			}
			controller = new AbortController()
			loadBlasts({ signal: controller.signal, background: true }).finally(() => {
				controller = null
			})
		}
		const intervalId = setInterval(refreshIfVisible, POLL_INTERVAL_MS)
		const handleVisibilityChange = () => {
			if (document.visibilityState === 'visible') refreshIfVisible()
		}
		document.addEventListener('visibilitychange', handleVisibilityChange)
		return () => {
			clearInterval(intervalId)
			controller?.abort()
			document.removeEventListener('visibilitychange', handleVisibilityChange)
		}
	}, [hasInProgressBlasts, loadBlasts])

	if (loading) return <Loader mt='xl' />

	const sortedBlasts = [...blasts].sort((a, b) => {
		const aTime = getBlastScheduledSendTime(a)
		const bTime = getBlastScheduledSendTime(b)
		if (!aTime) return bTime ? 1 : 0
		if (!bTime) return -1
		return bTime.getTime() - aTime.getTime()
	})

	return (
		<Stack spacing='md'>
			<Group position='apart'>
				<Title order={2}>Messaging</Title>
				{!loadError && (
					<Group spacing='xs'>
						<Button
							component={Link}
							to={`/${orgSlug}/experiences/${experienceSlug}/messaging/check-in-messages`}
							variant='default'
						>
							Check-in Messages
						</Button>
						<Button
							component={Link}
							to={`/${orgSlug}/experiences/${experienceSlug}/messaging/blasts/new`}
						>
							Create Blast
						</Button>
					</Group>
				)}
			</Group>
			{loadError && <Badge color='red'>{loadError}</Badge>}

			{!loadError && (
				<Table highlightOnHover striped>
					<thead>
						<tr>
							<th>Scheduled Send Time</th>
							<th>Channel</th>
							<th>Subject</th>
							<th>Status</th>
							<th>Recipients</th>
							<th>Undelivered</th>
							<th>Send Duration</th>
						</tr>
					</thead>
					<tbody>
						{sortedBlasts.map((b) => (
							<tr
								key={b.id}
								onClick={() =>
									navigate(
										`/${orgSlug}/experiences/${experienceSlug}/messaging/blasts/${b.id}`
									)
								}
								style={{ cursor: 'pointer' }}
							>
								<td>{getBlastScheduledSendTime(b)?.toLocaleString() || '—'}</td>
								<td>{b.channel}</td>
								<td>{renderSubject(b.subject)}</td>
								<td>
									{(() => {
										const normalizedStatus = String(b?.status || '').toUpperCase()
										const isInProgress = IN_PROGRESS_STATUSES.has(normalizedStatus)
										const {
											totalRecipients,
											queuedCount,
											processedCount,
											unsuccessfulCount,
										} = getBlastProgress(b)
										if (!isInProgress) {
											return <Badge>{b.status}</Badge>
										}
										const progressValue =
											totalRecipients > 0
												? (processedCount / totalRecipients) * 100
												: 0
										const progressLabel =
											totalRecipients > 0
												? `${processedCount.toLocaleString()} processed • ${queuedCount.toLocaleString()} queued${
														unsuccessfulCount > 0
															? ` • ${unsuccessfulCount.toLocaleString()} unsuccessful`
															: ''
												  }`
												: 'Preparing recipients'

										return (
											<Stack spacing={4} maw={320}>
												<Group spacing='xs'>
													<Badge>{b.status}</Badge>
													<Text size='xs' color='dimmed'>
														{progressLabel}
													</Text>
												</Group>
												<Progress
													value={progressValue}
													color='teal'
													radius='xl'
												/>
											</Stack>
										)
									})()}
								</td>
								<td>{getTotalRecipientsForBlast(b)}</td>
								<td>{renderUndelivered(b)}</td>
								<td>{formatSendDuration(b)}</td>
							</tr>
						))}
					</tbody>
				</Table>
			)}
		</Stack>
	)
}
